import { useState, useEffect } from 'react'
import { Copy, ExternalLink, Info, Check, Save, ChevronDown } from 'lucide-react'

// ─── Types ────────────────────────────────────────────────────────────────────

interface NilGoField {
  id: string
  category: string
  label: string
  value: string
  required: boolean
}

// Maps form field ids to the nilGoFields JSONB keys stored in the DB
export const FIELD_TO_NIL_GO_KEY: Record<string, string> = {
  brand_name: 'brandLegalName',
  total_compensation: 'totalCompensation',
  nature_of_relationship: 'natureOfRelationship',
  platforms: 'primaryPlatform',
  platforms_secondary: 'secondaryPlatform',
  deal_executed_at: 'agreementStartDate',
  end_date: 'agreementEndDate',
  service_description: 'serviceDescription',
  content_deadline: 'contentDeadline',
  usage_rights: 'usageRights',
  exclusivity: 'exclusivity',
  termination: 'terminationClause',
  payment_structure: 'paymentStructure',
  brand_contact_name: 'brandContactName',
  brand_contact_email: 'brandContactEmail',
}

export interface NilGoSubmissionFormProps {
  /** Persisted field values from the DB / contract scan */
  nilGoFields?: Record<string, string | null> | null
  /** Fallback brand name if not in nilGoFields */
  brandName?: string | null
  /** Fallback payout/compensation if not in nilGoFields */
  paymentAmount?: string | number | null
  /** Platform(s) for the deal */
  platform?: string | string[] | null
  /** Deal execution / agreement start date (ISO string) */
  dealExecutedAt?: string | null
  /** Agreement end date (ISO string) */
  endDate?: string | null
  /** Content due date (ISO string) */
  contentDueDate?: string | null
  /** Performance instructions / deliverables description */
  instructions?: string | null
  /** Called with the updated nilGoFields map when the user saves */
  onSave: (nilGoFields: Record<string, string>) => Promise<void>
  /** Label for the save button. Defaults to "Save Fields" */
  saveLabel?: string
  /** Whether to show the "Open NIL Go" action button. Defaults to false */
  showOpenNilGo?: boolean
  /** Override the info callout text */
  infoText?: string
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDate(isoString: string): string {
  return new Date(isoString).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })
}

function formatPayout(payout: string | number): string {
  const amount = typeof payout === 'number' ? payout : parseFloat(payout)
  if (isNaN(amount)) return String(payout)
  return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

function buildFields(props: NilGoSubmissionFormProps): NilGoField[] {
  const fields = props.nilGoFields ?? {}
  const brandName = props.brandName ?? ''
  const platform = props.platform ?? ''
  const dealExecutedAt = props.dealExecutedAt ?? ''
  const endDate = props.endDate ?? ''

  const startDate = fields.agreementStartDate ?? dealExecutedAt
  const contractEndDate = fields.agreementEndDate ?? endDate
  const resolvedBrandName = fields.brandLegalName ?? (brandName ? `${brandName} Inc.` : '')
  const resolvedCompensation = fields.totalCompensation
    ? formatPayout(fields.totalCompensation)
    : formatPayout(props.paymentAmount ?? '0')

  return [
    {
      id: 'brand_name',
      category: 'Basic Details',
      label: 'Brand/Company Name',
      value: resolvedBrandName,
      required: true,
    },
    {
      id: 'total_compensation',
      category: 'Basic Details',
      label: 'Compensation Amount',
      value: resolvedCompensation,
      required: true,
    },
    {
      id: 'nature_of_relationship',
      category: 'Basic Details',
      label: 'Deal Type',
      value: fields.natureOfRelationship ?? 'Social Media Endorsement',
      required: true,
    },
    {
      id: 'platforms',
      category: 'Platform Data',
      label: 'Primary Platform',
      value: fields.primaryPlatform ?? (Array.isArray(platform) ? (platform[0] ?? '') : platform),
      required: true,
    },
    {
      id: 'platforms_secondary',
      category: 'Platform Data',
      label: 'Secondary Platform',
      value:
        fields.secondaryPlatform ??
        (Array.isArray(platform) && platform.length > 1 ? platform[1] : ''),
      required: false,
    },
    {
      id: 'deal_executed_at',
      category: 'Dates',
      label: 'Agreement Start Date',
      value: startDate ? formatDate(startDate) : '',
      required: true,
    },
    {
      id: 'end_date',
      category: 'Dates',
      label: 'Agreement End Date',
      value: contractEndDate ? formatDate(contractEndDate) : '',
      required: true,
    },
    {
      id: 'service_description',
      category: 'Performance',
      label: 'Performance Deliverable',
      value: fields.serviceDescription ?? props.instructions ?? '',
      required: true,
    },
    {
      id: 'content_deadline',
      category: 'Performance',
      label: 'Content Deadline',
      value:
        fields.contentDeadline ?? (props.contentDueDate ? formatDate(props.contentDueDate) : ''),
      required: true,
    },
    {
      id: 'usage_rights',
      category: 'Rights & Restrictions',
      label: 'Usage Rights',
      value: fields.usageRights ?? '',
      required: true,
    },
    {
      id: 'exclusivity',
      category: 'Rights & Restrictions',
      label: 'Exclusivity Clause',
      value: fields.exclusivity ?? '',
      required: false,
    },
    {
      id: 'termination',
      category: 'Contract Terms',
      label: 'Termination Clause',
      value: fields.terminationClause ?? '',
      required: false,
    },
    {
      id: 'payment_structure',
      category: 'Contract Terms',
      label: 'Payment Schedule',
      value: fields.paymentStructure ?? '',
      required: true,
    },
    {
      id: 'brand_contact_name',
      category: 'Contact Info',
      label: 'Brand Contact Name',
      value: fields.brandContactName ?? '',
      required: true,
    },
    {
      id: 'brand_contact_email',
      category: 'Contact Info',
      label: 'Brand Contact Email',
      value: fields.brandContactEmail ?? '',
      required: true,
    },
  ]
}

// ─── Sub-components ───────────────────────────────────────────────────────────

const CATEGORY_COLOR = '#9CF0FB'

function FieldRow({
  field,
  onChange,
}: {
  field: NilGoField
  onChange: (id: string, value: string) => void
}) {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    await navigator.clipboard.writeText(field.value)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="py-3 border-b border-white/5 last:border-0">
      <div className="flex items-center justify-between mb-1.5">
        <p className="text-xs text-gray-400">
          {field.label}
          {field.required && <span className="text-[#C0FD72]/60 ml-1">*</span>}
        </p>
        <button
          onClick={handleCopy}
          className="shrink-0 p-1 text-gray-600 hover:text-gray-400 transition-colors"
          title="Copy to clipboard"
        >
          {copied ? (
            <Check className="w-3.5 h-3.5 text-[#C0FD72]" />
          ) : (
            <Copy className="w-3.5 h-3.5" />
          )}
        </button>
      </div>
      <textarea
        value={field.value}
        onChange={(e) => onChange(field.id, e.target.value)}
        placeholder="Not set"
        rows={1}
        className="w-full bg-white/5 border border-white/8 hover:border-white/15 focus:border-white/30 rounded-lg px-3 py-2 text-white text-base outline-hidden transition-colors placeholder:text-gray-600 resize-none overflow-hidden"
        style={{ fieldSizing: 'content' } as React.CSSProperties}
      />
    </div>
  )
}

function CategorySection({
  category,
  fields,
  onChange,
  defaultOpen = false,
}: {
  category: string
  fields: NilGoField[]
  onChange: (id: string, value: string) => void
  defaultOpen?: boolean
}) {
  const [open, setOpen] = useState(defaultOpen)
  const filledCount = fields.filter((f) => f.value.trim().length > 0).length

  return (
    <div className="bg-white/5 border border-white/8 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/5 transition-colors"
      >
        <div className="flex items-center gap-3 min-w-0">
          <span className="text-sm font-semibold" style={{ color: CATEGORY_COLOR }}>
            {category}
          </span>
          <span className="text-[11px] text-gray-500">
            {filledCount}/{fields.length} filled
          </span>
        </div>
        <ChevronDown
          className={`w-4 h-4 text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div className="px-4 pb-1">
          {fields.map((field) => (
            <FieldRow key={field.id} field={field} onChange={onChange} />
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Main export ─────────────────────────────────────────────────────────────

export const NilGoSubmissionForm = ({
  saveLabel = 'Save Fields',
  showOpenNilGo = false,
  infoText,
  onSave,
  ...rest
}: NilGoSubmissionFormProps) => {
  const [fieldList, setFieldList] = useState<NilGoField[]>(() => buildFields({ ...rest, onSave }))
  const [isSaving, setIsSaving] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [copiedAll, setCopiedAll] = useState(false)

  // Re-populate when nilGoFields changes (e.g. after a contract scan)
  useEffect(() => {
    setFieldList(buildFields({ ...rest, onSave }))
  }, [rest.nilGoFields]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleFieldChange = (id: string, value: string) => {
    setSaveSuccess(false)
    setFieldList((prev) => prev.map((f) => (f.id === id ? { ...f, value } : f)))
  }

  const handleCopyAll = async () => {
    const lines = fieldList
      .filter((f) => f.value.trim() !== '')
      .map((f) => `${f.label}: ${f.value}`)
      .join('\n')
    await navigator.clipboard.writeText(lines)
    setCopiedAll(true)
    setTimeout(() => setCopiedAll(false), 2500)
  }

  const handleSave = async () => {
    setIsSaving(true)
    setSaveSuccess(false)
    try {
      const nilGoFields: Record<string, string> = {}
      for (const field of fieldList) {
        const key = FIELD_TO_NIL_GO_KEY[field.id]
        if (key) nilGoFields[key] = field.value
      }
      await onSave(nilGoFields)
      setSaveSuccess(true)
      setTimeout(() => setSaveSuccess(false), 3000)
    } finally {
      setIsSaving(false)
    }
  }

  // Group fields by category
  const groups: { category: string; items: NilGoField[] }[] = []
  fieldList.forEach((f) => {
    const existing = groups.find((g) => g.category === f.category)
    if (existing) existing.items.push(f)
    else groups.push({ category: f.category, items: [f] })
  })

  const defaultInfoText =
    'The agent extracted these fields from the deal contract and your profile. Review each field — you can edit any value by clicking it. When ready, open NIL Go and paste each section.'

  return (
    <div className="bg-white/5 border border-white/8 rounded-2xl p-5 space-y-4">
      {/* Header */}
      <div>
        <p className="text-white font-semibold text-base">NIL Go Submission Details</p>
        <p className="text-gray-500 text-sm mt-0.5">
          Review and copy these fields to submit your collaboration to NIL Go
        </p>
      </div>

      {/* Info callout */}
      <div className="flex items-start gap-2.5 bg-[#9CF0FB]/5 border border-[#9CF0FB]/15 rounded-xl px-4 py-3">
        <Info className="w-4 h-4 text-[#9CF0FB] shrink-0 mt-0.5" />
        <p className="text-gray-400 text-xs leading-relaxed">{infoText ?? defaultInfoText}</p>
      </div>

      {/* Fields grouped by category */}
      <div className="space-y-2">
        {groups.map((g, i) => (
          <CategorySection
            key={g.category}
            category={g.category}
            fields={g.items}
            onChange={handleFieldChange}
            defaultOpen={i === 0}
          />
        ))}
      </div>

      {/* Action buttons */}
      <div className="flex flex-col gap-2.5">
        <button
          onClick={handleCopyAll}
          className="w-full flex items-center justify-center gap-2 bg-white/8 border border-white/15 hover:border-white/30 text-white text-sm font-semibold rounded-xl py-3 transition-all"
        >
          {copiedAll ? (
            <>
              <Check className="w-4 h-4 text-[#C0FD72]" />
              <span className="text-[#C0FD72]">Copied to Clipboard</span>
            </>
          ) : (
            <>
              <Copy className="w-4 h-4" />
              Copy All Fields
            </>
          )}
        </button>

        <button
          onClick={handleSave}
          disabled={isSaving}
          className="w-full flex items-center justify-center gap-2 bg-white/8 border border-white/15 hover:border-white/30 text-white text-sm font-semibold rounded-xl py-3 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {saveSuccess ? (
            <>
              <Check className="w-4 h-4 text-[#C0FD72]" />
              <span className="text-[#C0FD72]">Saved</span>
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              {isSaving ? 'Saving…' : saveLabel}
            </>
          )}
        </button>

        {showOpenNilGo && (
          <a
            href="https://nilgo.com"
            target="_blank"
            rel="noopener noreferrer"
            className="w-full flex items-center justify-center gap-2 bg-[#C0FD72] hover:bg-[#d2ff9c] text-black text-sm font-semibold rounded-xl py-3 transition-colors"
          >
            Open NIL Go
            <ExternalLink className="w-4 h-4" />
          </a>
        )}
      </div>
    </div>
  )
}
